import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks'
import selectUserVehicles from '@/selectors/selectUserVehicles'
import { vehicleChargersCollection } from '@/reducers/vehicleChargers'
import { vehicleActiveChargeSettingsCollection } from '@/reducers/vehicleActiveChargeSettings'
import { chargerActiveChargeSettingsCollection } from '@/reducers/chargerActiveChargeSettings'
import {
  programsNewCollection,
  selectEnrolledProgram,
} from '@/reducers/programsNew'
import { ActiveChargeSettingsFields } from '@/types/activeChargeSettings'

export interface ShadowRateProgram {
  name: string
  /** Display sponsor; `null` when the program has no sponsor name configured. */
  sponsorName: string | null
}

/**
 * A device's rate signal is "shadowed" when an enrolled program prescribes a
 * rate plan *other than* the user's own selected plan — primary or secondary.
 * A program that explicitly prescribes the plan the user already selected isn't
 * a shadow rate (the optimization target matches what the user expects), so it
 * does not trigger the alert. The `program_id` of whichever rate plan qualifies
 * (primary preferred) identifies the program doing the prescribing.
 */
function prescribingProgramId(
  settings: ActiveChargeSettingsFields | undefined,
  selectedPlanId: number | null,
): number | null {
  if (!settings) {
    return null
  }
  // Optional chaining guards against a settings response that predates the
  // backend rate-plan fields (deploy-ordering safety). A prescribed plan only
  // "shadows" the user when it differs from their own selected plan.
  if (
    settings.primary_rate_plan?.is_program_prescribed &&
    settings.primary_rate_plan.value !== selectedPlanId
  ) {
    return settings.primary_rate_plan.program_id
  }
  if (
    settings.secondary_rate_plan?.is_program_prescribed &&
    settings.secondary_rate_plan.value !== selectedPlanId
  ) {
    return settings.secondary_rate_plan.program_id
  }
  return null
}

/**
 * Aggregates per-device active charge settings across all of the user's
 * vehicles and chargers and, if any device is being optimized to a
 * program-prescribed rate signal, resolves the prescribing program's name and
 * sponsor for the shadow-rate alert on the Manage Utility Information page.
 *
 * The page is global (not device-scoped), so we fetch each device's settings
 * and aggregate. Fetching mirrors `usePrescribedChargers` — `Promise.all` over
 * `actions.fetch` (calling `useFetch` inside a `.map` would break the rules of
 * hooks). A failed fetch is treated as "not prescribed" so a flaky request
 * never surfaces a misleading alert. The prescribing program is derived from
 * the fetched settings and the user's selected plan, so a plan change re-derives
 * without re-fetching every device.
 *
 * If multiple distinct programs prescribe a rate across devices, the first one
 * found wins — the alert copy is singular and the common case is one program.
 */
export function useShadowRateAlert(): {
  prescribedProgram: ShadowRateProgram | null
  isLoading: boolean
} {
  const dispatch = useAppDispatch()
  const vehicles = useAppSelector(selectUserVehicles)
  const { data: chargers, isLoadingOrIdle: chargersLoading } =
    vehicleChargersCollection.useFetch()
  // Populate the programs store so we can resolve name + sponsor by `program_id`.
  // `programs/new` includes the user's *enrolled* programs — the prescribing
  // program is one they're enrolled in — unlike `utility_programs`, which only
  // lists programs still available to enroll in.
  programsNewCollection.useFetch()
  // The plan the user selected for themselves. A program prescribing this same
  // plan isn't a shadow rate, so we compare against it below.
  const selectedPlanId = useAppSelector(
    (state) => state.plans?.selectedPlan?.id ?? null,
  )

  const [deviceSettings, setDeviceSettings] = useState<
    Array<ActiveChargeSettingsFields | undefined>
  >([])
  const [isResolving, setIsResolving] = useState(true)

  const vehicleIds = useMemo(() => vehicles.map((v) => v.id), [vehicles])
  const chargerIds = useMemo(
    () => (chargers ?? []).map((c) => c.id),
    [chargers],
  )

  // Stable dependency: only re-run when the *set* of device ids changes.
  const deviceKey = useMemo(
    () =>
      [
        'v',
        ...[...vehicleIds].sort((a, b) => a - b),
        'c',
        ...[...chargerIds].sort((a, b) => a - b),
      ].join(','),
    [vehicleIds, chargerIds],
  )

  useEffect(() => {
    let cancelled = false

    if (vehicleIds.length === 0 && chargerIds.length === 0) {
      setDeviceSettings([])
      setIsResolving(false)
      return
    }

    setIsResolving(true)

    // The fetch action creator accepts `{ id }` and appends `/<id>` to the
    // configured path. The dispatched action is async (resolves with the
    // success/failure FSA), so we cast through `any` to satisfy redux's
    // narrower thunk signature — same convention as `usePrescribedChargers`.
    const fetchSettings = async (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      collection: { actions: { fetch: (args: { id: number }) => any } },
      id: number,
    ): Promise<ActiveChargeSettingsFields | undefined> => {
      const action = (await dispatch(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        collection.actions.fetch({ id }) as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      )) as { type?: string; payload?: any } | undefined
      // On failure the action type contains "FAILURE"; treat as "not prescribed".
      const isFailure =
        typeof action?.type === 'string' && action.type.includes('FAILURE')
      return isFailure
        ? undefined
        : (action?.payload as ActiveChargeSettingsFields | undefined)
    }

    Promise.all([
      ...vehicleIds.map((id) =>
        fetchSettings(vehicleActiveChargeSettingsCollection, id),
      ),
      ...chargerIds.map((id) =>
        fetchSettings(chargerActiveChargeSettingsCollection, id),
      ),
    ]).then((allSettings) => {
      if (cancelled) {
        return
      }
      setDeviceSettings(allSettings)
      setIsResolving(false)
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, deviceKey])

  const prescribedProgramId = useMemo(
    () =>
      deviceSettings
        .map((settings) => prescribingProgramId(settings, selectedPlanId))
        .find((id): id is number => id != null) ?? null,
    [deviceSettings, selectedPlanId],
  )

  // Recreate the parameterized selector only when the program id changes so its
  // memoization survives unrelated re-renders.
  const selectPrescribingProgram = useMemo(
    () =>
      prescribedProgramId != null
        ? selectEnrolledProgram(prescribedProgramId)
        : null,
    [prescribedProgramId],
  )
  const program = useAppSelector((state) =>
    selectPrescribingProgram ? selectPrescribingProgram(state) : null,
  )

  const prescribedProgram = useMemo<ShadowRateProgram | null>(() => {
    if (prescribedProgramId == null) {
      return null
    }
    // CMS view config is preferred for display; fall back to the program's own
    // name so the alert still renders if the CMS config hasn't loaded. Use `||`
    // (not `??`) so an empty CMS string also falls back.
    const name =
      program?.view_config_json?.name || program?.utility_program?.name
    if (!name) {
      return null
    }
    // Only a real sponsor name; an empty/missing value yields `null` so the
    // component renders generic copy instead of a blank.
    const sponsorName = program?.view_config_json?.sponsorName || null
    return { name, sponsorName }
  }, [prescribedProgramId, program])

  return { prescribedProgram, isLoading: isResolving || chargersLoading }
}
